import type { HeroTimeEntry, HeroUsageMap } from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetRowRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  heroTimeEntryHasFpMarker,
  isNwPlusFpAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";
import {
  getHeroAggregateUsageStatus,
  getHeroPartnerName,
  getHeroUsedTimesheetInfo,
  type AggregatedHeroTimeEntry,
} from "@/features/timesheetFlow/lib/heroTimesAggregation";
import type { SuggestionItem } from "@/features/timesheetFlow/components/SuggestionPickerDialog";

function isPauseEntry(entry: HeroTimeEntry): boolean {
  return /pause/i.test(String(entry.category_name || ""));
}

function toLocalDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatTimeFromDate(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function addSecondsToTime(time: string, seconds: number): string {
  const parts = time.split(":").map(Number);
  const h = parts[0];
  const m = parts[1];
  if (h === undefined || m === undefined || !Number.isFinite(h) || !Number.isFinite(m)) return "";
  const total = h * 3600 + m * 60 + seconds;
  const nh = Math.floor((total / 3600) % 24);
  const nm = Math.floor((total % 3600) / 60);
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

export interface HeroUsageStatus {
  isFull: boolean;
  isPartial: boolean;
  isFree: boolean;
}

export function getHeroUsageStatus(
  uuid: string | undefined,
  usage: HeroUsageMap,
  currentTimesheetId: number | null,
): HeroUsageStatus {
  if (!uuid) return { isFull: false, isPartial: false, isFree: true };
  const u = usage[uuid];
  if (!u) return { isFull: false, isPartial: false, isFree: true };
  const status = String(u.status || "").toLowerCase();
  if (status === "free") return { isFull: false, isPartial: false, isFree: true };
  if (u.used_timesheet_id != null && currentTimesheetId != null && u.used_timesheet_id === currentTimesheetId) {
    return { isFull: false, isPartial: true, isFree: false };
  }
  if (status === "full" || status === "used") return { isFull: true, isPartial: false, isFree: false };
  if (status === "partial") return { isFull: false, isPartial: true, isFree: false };
  return { isFull: false, isPartial: false, isFree: true };
}

export function heroEntryToRow(
  entry: HeroTimeEntry | AggregatedHeroTimeEntry,
  showTimeRange: boolean,
): TimesheetRowRecord | null {
  if (isPauseEntry(entry)) return null;

  const uuid = entry._uuid;
  let dateStr = "";
  let timeFrom = "";
  let timeTo = "";
  if (entry.start) {
    const d = new Date(entry.start);
    if (!Number.isNaN(d.getTime())) {
      dateStr = toLocalDate(entry.start);
      if (showTimeRange) {
        timeFrom = formatTimeFromDate(d);
        if (entry.duration_in_seconds != null) {
          timeTo = addSecondsToTime(timeFrom, Number(entry.duration_in_seconds));
        }
      }
    }
  }

  let hours = 0;
  if (entry.duration_in_seconds != null) {
    hours = Number(entry.duration_in_seconds) / 3600;
  }

  const person = getHeroPartnerName(entry);
  const comment = entry.comment || entry.category_name || "";
  const agg = entry as AggregatedHeroTimeEntry;
  const trackingUuids = agg.uuids?.length ? agg.uuids : uuid ? [uuid] : undefined;
  const usedSecondsByUuid =
    agg.uuid_durations && Object.keys(agg.uuid_durations).length
      ? { ...agg.uuid_durations }
      : undefined;

  return {
    work_date: dateStr,
    person_name: person === "Unbekannt" ? "" : person,
    hours_decimal: Math.round(hours * 100) / 100,
    time_from: showTimeRange ? timeFrom || null : null,
    time_to: showTimeRange ? timeTo || null : null,
    comment,
    hero_uuid: uuid || null,
    hero_row_key: agg.row_key || uuid || undefined,
    hero_uuids: trackingUuids,
    hero_used_seconds_by_uuid: usedSecondsByUuid,
  };
}

export function importHeroTimesToRows(
  items: AggregatedHeroTimeEntry[],
  options: {
    auftragsart: string | null;
    usage: HeroUsageMap;
    editableInThisTimesheet: Set<string>;
    showTimeRange: boolean;
    existingRows: TimesheetRowRecord[];
    locallyFreed: Set<string>;
  },
): TimesheetRowRecord[] {
  const usedAggregates = new Set<string>();
  for (const row of options.existingRows) {
    if (row.hero_uuid) usedAggregates.add(row.hero_uuid);
    if (row.hero_row_key) usedAggregates.add(row.hero_row_key);
  }

  const added: TimesheetRowRecord[] = [];

  for (const it of items) {
    if (isNwPlusFpAuftragsart(options.auftragsart) && heroTimeEntryHasFpMarker(it)) continue;
    if (isPauseEntry(it)) continue;

    const aggregateKey = it._uuid ?? null;
    const isLocallyFreed = aggregateKey ? options.locallyFreed.has(aggregateKey) : false;
    // Locally freed only allows re-import within this sheet – never if claimed elsewhere.
    const usedByOtherSheets = getHeroAggregateUsageStatus(
      it,
      options.usage,
      options.editableInThisTimesheet,
    ).isFull;

    if (usedByOtherSheets) continue;
    if (aggregateKey && !isLocallyFreed && usedAggregates.has(aggregateKey)) continue;

    const row = heroEntryToRow(it, options.showTimeRange);
    if (!row || !row.work_date) continue;
    added.push(row);
    if (aggregateKey) usedAggregates.add(aggregateKey);
  }

  return added;
}

export function buildHeroSuggestionItems(
  items: AggregatedHeroTimeEntry[],
  options: {
    auftragsart: string | null;
    usage: HeroUsageMap;
    editableInThisTimesheet: Set<string>;
    locallyFreed: Set<string>;
    usedInCurrentDraft: Set<string>;
    currentTimesheetId: number | null;
  },
): SuggestionItem[] {
  const selectableItems = isNwPlusFpAuftragsart(options.auftragsart)
    ? items.filter((it) => !heroTimeEntryHasFpMarker(it))
    : items;

  return selectableItems.map((it) => {
    const id = it._uuid;
    const person = getHeroPartnerName(it);
    const dateStr = it.start ? new Date(it.start).toLocaleDateString("de-DE") : "";
    const hours = ((it.duration_in_seconds || 0) / 3600).toFixed(2).replace(".", ",");
    const timeStr = it.start
      ? new Date(it.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
      : "";
    const category = it.category_name || "";

    const usedInDraft = options.usedInCurrentDraft.has(id);
    const usageStatus = getHeroAggregateUsageStatus(it, options.usage, options.editableInThisTimesheet);
    const isUsedFully =
      it.usage_state === "used" ? true : it.usage_state === "remaining" ? false : usageStatus.isFull;
    // Keep claimed hours locked even if they were locally removed from this draft.
    const disabled = usedInDraft || isUsedFully;
    let disabledReason: string | undefined;

    if (usedInDraft) {
      disabledReason = "Bereits übernommen";
    } else if (isUsedFully) {
      const usedInfo = getHeroUsedTimesheetInfo(it, options.usage, options.editableInThisTimesheet);
      const usedNumber = usedInfo?.display_number || usedInfo?.timesheet_number;
      if (
        usedInfo?.used_timesheet_id != null &&
        options.currentTimesheetId != null &&
        usedInfo.used_timesheet_id === options.currentTimesheetId
      ) {
        disabledReason = "Bereits in diesem Stundennachweis";
      } else if (usedNumber) {
        disabledReason = `Bereits auf Stundennachweis Nr.${usedNumber}`;
      } else {
        disabledReason = "Bereits auf Stundennachweis";
      }
    }

    const metaParts = [timeStr, category].filter(Boolean);
    const meta = metaParts.length ? metaParts.join(" · ") : it.comment || undefined;

    return {
      id,
      label: `${dateStr} · ${person} · ${hours} h`,
      meta,
      disabled,
      disabledReason,
    };
  });
}

/** Looks like an aggregate key (`personId__YYYY-MM-DD`), not a Hero tracking UUID. */
function isHeroAggregateKey(value: string | null | undefined): boolean {
  if (!value) return false;
  return /__\d{4}-\d{2}-\d{2}/.test(value) || value.endsWith("__used") || value.endsWith("__remaining");
}

function allocateUsedSecondsByUuid(
  uuids: string[],
  durationMap: Record<string, number>,
  aggregateDurationSeconds: number,
  usedSecondsTotal: number,
): Record<string, number> {
  const durationValues = Object.values(durationMap).filter((val) => Number.isFinite(val));
  const totalDuration = durationValues.length
    ? durationValues.reduce((sum, val) => sum + Number(val), 0)
    : Math.round(Number(aggregateDurationSeconds) || 0);

  const effectiveTotal =
    totalDuration > 0 ? Math.min(usedSecondsTotal, totalDuration) : usedSecondsTotal;

  const usedSecondsByUuid: Record<string, number> = {};
  if (uuids.length === 1) {
    usedSecondsByUuid[uuids[0]!] = effectiveTotal;
    return usedSecondsByUuid;
  }

  if (totalDuration > 0) {
    const ratio = effectiveTotal / totalDuration;
    const allocations = uuids.map((uuid) => {
      const duration = Number.isFinite(durationMap[uuid])
        ? Number(durationMap[uuid])
        : totalDuration / uuids.length;
      const rawUsed = Math.round(duration * ratio);
      return {
        uuid,
        duration,
        used: Math.min(rawUsed, duration),
      };
    });

    let assigned = allocations.reduce((sum, entry) => sum + entry.used, 0);
    let diff = effectiveTotal - assigned;
    if (diff !== 0) {
      const direction = diff > 0 ? 1 : -1;
      let remaining = Math.abs(diff);
      for (const entry of allocations) {
        const capacity = direction > 0 ? entry.duration - entry.used : entry.used;
        const delta = Math.min(capacity, remaining);
        if (delta > 0) {
          entry.used += direction * delta;
          remaining -= delta;
        }
        if (remaining <= 0) break;
      }
    }

    for (const entry of allocations) {
      usedSecondsByUuid[entry.uuid] = entry.used;
    }
    return usedSecondsByUuid;
  }

  const perUuid = uuids.length ? Math.round(effectiveTotal / uuids.length) : 0;
  for (const uuid of uuids) {
    usedSecondsByUuid[uuid] = perUuid;
  }
  return usedSecondsByUuid;
}

/**
 * Collect Hero usage claims for POST /hero-usage.
 * Resolves aggregate keys (`person__date`) to real tracking UUIDs via legacyAggregates
 * (same behavior as classic stundennachweise.js collectHeroUsageByRow).
 */
export function collectHeroUsageFromRows(
  rows: TimesheetRowRecord[],
  legacyAggregates: AggregatedHeroTimeEntry[] = [],
): Array<{
  row_key: string;
  uuids: string[];
  used_seconds_by_uuid: Record<string, number>;
}> {
  const result: Array<{
    row_key: string;
    uuids: string[];
    used_seconds_by_uuid: Record<string, number>;
  }> = [];

  for (const row of rows) {
    const rowKey = row.hero_row_key || row.hero_uuid;
    if (!rowKey) continue;

    const legacyAgg =
      legacyAggregates.find((it) => it._uuid === rowKey || it.row_key === rowKey) ||
      (row.hero_uuid
        ? legacyAggregates.find((it) => it._uuid === row.hero_uuid || it.row_key === row.hero_uuid)
        : undefined);

    let uuids: string[] = [];
    let durationMap: Record<string, number> = {};
    let aggregateDuration = 0;

    if (legacyAgg?.uuids?.length) {
      uuids = [...legacyAgg.uuids];
      durationMap = legacyAgg.uuid_durations || {};
      aggregateDuration = Number(legacyAgg.duration_in_seconds) || 0;
    } else if (row.hero_uuids?.length) {
      uuids = row.hero_uuids.filter((u) => u && !isHeroAggregateKey(u));
      durationMap = row.hero_used_seconds_by_uuid || {};
      aggregateDuration = Object.values(durationMap).reduce(
        (sum, val) => sum + (Number.isFinite(val) ? Number(val) : 0),
        0,
      );
    } else if (row.hero_uuid && !isHeroAggregateKey(row.hero_uuid)) {
      uuids = [row.hero_uuid];
      durationMap = row.hero_used_seconds_by_uuid || {};
      aggregateDuration = Number(durationMap[row.hero_uuid]) || 0;
    }

    // Never persist aggregate keys as hero_uuid – that unlocks hours on other sheets.
    if (!uuids.length) continue;

    const hoursValue = Number(row.hours_decimal);
    const usedSecondsTotal =
      Number.isFinite(hoursValue) && hoursValue > 0
        ? Math.round(hoursValue * 3600)
        : aggregateDuration;

    const usedSecondsByUuid =
      row.hero_used_seconds_by_uuid &&
      uuids.every((u) => Number.isFinite(row.hero_used_seconds_by_uuid?.[u]))
        ? Object.fromEntries(
            uuids.map((u) => [u, Math.round(Number(row.hero_used_seconds_by_uuid![u]))]),
          )
        : allocateUsedSecondsByUuid(uuids, durationMap, aggregateDuration, usedSecondsTotal);

    result.push({
      row_key: legacyAgg?.row_key || row.hero_row_key || rowKey,
      uuids,
      used_seconds_by_uuid: usedSecondsByUuid,
    });
  }

  return result;
}

export function recalcPeriodFromRows(rows: TimesheetRowRecord[]): {
  dateFrom: string;
  dateTo: string;
} | null {
  const dates = rows.map((r) => r.work_date).filter(Boolean).sort();
  if (!dates.length) return null;
  return { dateFrom: dates[0]!, dateTo: dates[dates.length - 1]! };
}

/** Pro geleisteter Stunde ein Achtel (Summe aller Zeilen, aufgerundet). */
export function recalcTravelEighthsFromRows(rows: TimesheetRowRecord[]): number {
  let totalHours = 0;
  for (const row of rows) {
    const hoursValue = Number(row.hours_decimal);
    if (!Number.isFinite(hoursValue) || hoursValue <= 0) continue;
    totalHours += hoursValue;
  }
  return totalHours > 0 ? Math.ceil(totalHours) : 0;
}
